/**
 * Locale-aware path helpers.
 *
 * Internal links must preserve the active `/:lang` prefix so navigating while
 * in English keeps you in English. the default locale and has NO prefix.
 *
 *   localePath('/about', SECONDARY) === '/<secondary>/about'
 *   localePath('/about', DEFAULT)   === '/about'
 *   localePath('/',      SECONDARY) === '/<secondary>'
 *
 * Use `useLocalePath()` inside components to bind to the current locale.
 */
import { useCallback } from 'react'
import { DEFAULT_LOCALE, LOCALES, type Locale } from './messages'
import { useTranslation } from './LocaleContext'

/** Prefix an app-internal path with the locale segment (none for the default locale). */
export function localePath(path: string, locale: Locale): string {
  const normalized = path.startsWith('/') ? path : `/${path}`
  if (locale === DEFAULT_LOCALE) return normalized
  // `/` → `/en`, `/about` → `/en/about`
  return normalized === '/' ? `/${locale}` : `/${locale}${normalized}`
}

const escapeRe = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
const LOCALE_PREFIX = new RegExp(`^/(${LOCALES.map(escapeRe).join('|')})(?=/|$)`)

/** Strip a leading locale segment from a pathname, returning the bare path. */
export function stripLocale(pathname: string): string {
  const m = pathname.match(LOCALE_PREFIX)
  if (!m) return pathname || '/'
  const rest = pathname.slice(m[0].length)
  return rest || '/'
}

/** Hook: prefix a path with the CURRENT locale. */
export function useLocalePath(): (path: string) => string {
  const { locale } = useTranslation()
  return useCallback((path: string) => localePath(path, locale), [locale])
}
